import { useQueryState } from 'nuqs';
import type { AccountDrilldownTab } from '@shared/flow/account-drilldown-tabs';
import { ACCOUNT_TAB_PARAM } from '../../../../../../mini-apps/_shared/flow-links';
import { flowUrlParams } from '../../../../../../mini-apps/_shared/url-params';

/**
 * The URL's raw `?accountTab=`, parsed against every drilldown tab and not yet resolved against a
 * layout. For callers that carry it to another account (J/K). Writes replace the history entry.
 */
export function useLinkedAccountTab(): [AccountDrilldownTab | null, (next: AccountDrilldownTab | null) => void] {
  const [linkedTab, setLinkedTab] = useQueryState(
    ACCOUNT_TAB_PARAM,
    flowUrlParams.accountTab.withOptions({ history: 'replace' }),
  );
  return [linkedTab, next => void setLinkedTab(next)];
}

/**
 * The tab an account drilldown shows: the URL's `?accountTab=` resolved against the tabs this
 * layout declares.
 *
 * Layouts declare; behavior is shared. A layout (V1 drilldown, Flow lens, Growth Strategist lens)
 * only declares which tabs it offers and its default (`tabs`, `fallback`), and gets back a tab typed
 * as one of those. Reading the param, writing it on a tab click and ignoring a tab the layout lacks
 * all live here, never in a per-role copy.
 *
 * Contract:
 *  - The URL is the only source of truth. No tab state, seed, effect or ref (StrictMode-safe).
 *  - A param naming a tab the layout lacks (or a value that is not a tab) shows `fallback`. The
 *    param is left in the URL and nothing is written.
 *  - `setTab` replaces the history entry and leaves other params alone. The default tab is never
 *    written on mount.
 *
 * The classic account page (`lib/urlParams.ts`) has its own `accountTab` key; it is a separate
 * page and the two never share a mounted route.
 */
export function useAccountTab<T extends AccountDrilldownTab>(
  tabs: readonly T[],
  fallback: T,
): { tab: T; setTab: (tab: T) => void } {
  const [linkedTab, setLinkedTab] = useLinkedAccountTab();
  const tab = tabs.find(candidate => candidate === linkedTab) ?? fallback;
  return { tab, setTab: setLinkedTab };
}

/** `href` with `?accountTab=<tab>` appended (`&` when it already has a query); `href` as is for null. */
export function withAccountTab(href: string, tab: AccountDrilldownTab | null): string {
  if (!tab) return href;
  return `${href}${href.includes('?') ? '&' : '?'}${ACCOUNT_TAB_PARAM}=${tab}`;
}
